import React, { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { EvalApiService } from '../../services/EvalApiService';
import { PrInferenceListItem } from '../../types';
import { fmtIso } from '../../utils';

const PAGE_SIZE = 50;

export const InferencePickerPage: React.FC = () => {
  const { token } = useAuthContext();

  const [items, setItems] = useState<PrInferenceListItem[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const [filterStoreId, setFilterStoreId] = useState('');
  const [filterChainId, setFilterChainId] = useState('');
  const [filterCameraId, setFilterCameraId] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ingesting, setIngesting] = useState(false);
  const [ingestResult, setIngestResult] = useState<{ created: number; skipped: number } | null>(
    null
  );

  const load = useCallback(() => {
    setLoading(true);
    setIngestResult(null);
    EvalApiService.listPrInferences(token ?? '', {
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
      store_id: filterStoreId ? Number(filterStoreId) : undefined,
      chain_id: filterChainId ? Number(filterChainId) : undefined,
      camera_id: filterCameraId ? Number(filterCameraId) : undefined,
    })
      .then(({ items, has_next }) => {
        setItems(items);
        setHasNext(has_next);
      })
      .catch(() => {
        /* errors handled by ApiService */
      })
      .finally(() => setLoading(false));
  }, [token, page, filterStoreId, filterChainId, filterCameraId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange =
    (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value.replace(/\D/g, ''));
      setPage(0);
      setSelected(new Set());
    };

  const handleIngest = async () => {
    setIngesting(true);
    try {
      const result = await EvalApiService.bulkIngestFromPrInferences(
        token ?? '',
        Array.from(selected)
      );
      setIngestResult({ created: result.created.length, skipped: result.skipped.length });
      setSelected(new Set());
      load();
    } finally {
      setIngesting(false);
    }
  };

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">Import Inference</div>
          <div className="eval-page-sub">Page {page + 1} · newest first</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {ingestResult && (
            <span
              style={{
                fontSize: 12,
                color: ingestResult.created > 0 ? 'var(--positive)' : 'var(--text-secondary)',
              }}
            >
              {ingestResult.created} added
              {ingestResult.skipped > 0 ? `, ${ingestResult.skipped} already existed` : ''}
            </span>
          )}
          {selected.size >= 1 && (
            <button className="eval-btn" disabled={ingesting} onClick={handleIngest}>
              {ingesting ? 'Adding…' : `Add to Eval (${selected.size})`}
            </button>
          )}
        </div>
      </div>

      <div className="eval-filters">
        <input
          className="eval-form-input"
          placeholder="store_id"
          value={filterStoreId}
          onChange={handleFilterChange(setFilterStoreId)}
          style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
        <input
          className="eval-form-input"
          placeholder="chain_id"
          value={filterChainId}
          onChange={handleFilterChange(setFilterChainId)}
          style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
        <input
          className="eval-form-input"
          placeholder="camera_id"
          value={filterCameraId}
          onChange={handleFilterChange(setFilterCameraId)}
          style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      ) : (
        <>
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Inference ID</th>
                <th>Chain</th>
                <th>Store</th>
                <th>Camera</th>
                <th>Image</th>
                <th title="Products">#Prod</th>
                <th title="Cartons">#Cart</th>
                <th title="Mods">#Mod</th>
                <th title="Price Tags">#PT</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.inference_id}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(item.inference_id)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          e.target.checked
                            ? next.add(item.inference_id)
                            : next.delete(item.inference_id);
                          return next;
                        });
                      }}
                    />
                  </td>
                  <td
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}
                    title={item.inference_id}
                  >
                    {item.inference_id.slice(-8)}
                  </td>
                  <td style={{ fontSize: 12 }}>{item.chain_id ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{item.store_id ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{item.camera_id ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{item.image_id ?? '—'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'right' }}>
                    {item.products_count}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'right' }}>
                    {item.cartons_count}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'right' }}>
                    {item.mods_count}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'right' }}>
                    {item.price_tags_count}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {fmtIso(item.created_at)}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={11}
                    style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}
                  >
                    No inferences found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {(page > 0 || hasNext) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginTop: 16,
                fontSize: 13,
              }}
            >
              <button
                className="eval-btn-ghost"
                disabled={page === 0}
                onClick={() => {
                  setPage((p) => p - 1);
                  setSelected(new Set());
                }}
              >
                ← Prev
              </button>
              <span style={{ color: 'var(--text-secondary)' }}>Page {page + 1}</span>
              <button
                className="eval-btn-ghost"
                disabled={!hasNext}
                onClick={() => {
                  setPage((p) => p + 1);
                  setSelected(new Set());
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
